import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

const features = [
  {
    icon: "ri-camera-3-line",
    title: "Body-Worn Camera Systems",
    description:
      "Hytera body cameras deliver HD/4K video evidence capture, GPS tagging, night vision, and pre-record buffers — keeping officers accountable and courts well-equipped with irrefutable evidence.",
  },
  {
    icon: "ri-eye-line",
    title: "Live Monitoring & Analytics",
    description:
      "Hytera body cameras stream live video to command centres in real time, enabling supervisors to monitor incidents as they unfold and dispatch support faster — dramatically reducing response times.",
  },
  {
    icon: "ri-radio-line",
    title: "Mission-Critical Communications",
    description:
      "Hytera digital radios and body cameras integrate seamlessly into unified command platforms, ensuring field teams and command centres stay in sync with encrypted, interference-free communication.",
  },
  {
    icon: "ri-cloud-line",
    title: "Evidence Management Platforms",
    description:
      "Securely upload, tag, search, and audit all captured footage through Hytera's DEMS evidence management platform — fully compliant with digital chain-of-custody requirements.",
  },
  {
    icon: "ri-wifi-line",
    title: "Live Streaming & Remote Monitoring",
    description:
      "Stream live video from body cameras and fixed IP cameras directly to command centres in real time, enabling faster decisions during high-stakes incidents.",
  },
  {
    icon: "ri-shield-check-line",
    title: "End-to-End Encryption",
    description:
      "All recordings and transmissions are protected by AES-256 encryption, ensuring sensitive footage remains secure from capture through storage and review.",
  },
];

const products = [
  {
    brand: "Hytera",
    relationship: "distributor" as const,
    name: "SC880 5G Body Camera",
    spec: "5G · 4K · 300s Pre-Record · IP68 · MIL-STD-810G · 10-hr battery",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/0cb7f09f1fcbcac9b09d7997834340e7.png",
  },
  {
    brand: "Hytera",
    relationship: "distributor" as const,
    name: "SC780 4G Body Camera",
    spec: "4G LTE · 1080P · 130° FOV · 13-hr battery · 120s Pre-Record · IP68",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/ce581c5e95e949b46179572a17de2edb.webp",
  },
  {
    brand: "Hytera",
    relationship: "distributor" as const,
    name: "GC550 Body Camera",
    spec: "1080P · 8-hr battery · IP67 · MIL-STD-810G · IR Night Vision · AES-256",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/4c9b9e898793c8c887b16e91f8744d95.png",
  },
  {
    brand: "Hytera",
    relationship: "distributor" as const,
    name: "DEMS Evidence Platform",
    spec: "Cloud · Chain-of-Custody · AI Search · Audit Trail",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/ff225e43a3f5ab8ff4c9c58e9937c81d.png",
  },
];

const industries = [
  { icon: "ri-shield-user-line", name: "Law Enforcement" },
  { icon: "ri-building-2-line", name: "Corporate Security" },
  { icon: "ri-government-line", name: "Government Facilities" },
  { icon: "ri-store-line", name: "Retail & Commercial" },
  { icon: "ri-hospital-line", name: "Healthcare Premises" },
  { icon: "ri-roadster-line", name: "Traffic & Transport" },
];

const stats = [
  { value: "1,200+", label: "Officers Equipped" },
  { value: "68%", label: "Reduction in evidence disputes" },
  { value: "1", label: "Authorized Brand" },
  { value: "24/7", label: "Monitoring Capability" },
];

const surveillanceStructuredData = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sedistribution.com.my/" },
      { "@type": "ListItem", "position": 2, "name": "Solutions", "item": "https://www.sedistribution.com.my/solutions" },
      { "@type": "ListItem", "position": 3, "name": "Surveillance & Security", "item": "https://www.sedistribution.com.my/solutions/surveillance" }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Surveillance & Security Solutions Malaysia | Hytera Body Camera | SE Distribution",
    "description": "SE Distribution is Malaysia's authorized Hytera distributor — SC880 5G (4K, 300s pre-record), SC780 4G (13-hr battery, live streaming), GC550 (compact, IP67, IR night vision). DEMS digital evidence management. AES-256 encryption. KL-based support team.",
    "url": "https://www.sedistribution.com.my/solutions/surveillance",
    "inLanguage": "en-MY",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sedistribution.com.my/" },
        { "@type": "ListItem", "position": 2, "name": "Solutions", "item": "https://www.sedistribution.com.my/solutions" },
        { "@type": "ListItem", "position": 3, "name": "Surveillance & Security", "item": "https://www.sedistribution.com.my/solutions/surveillance" }
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "SE Distribution Sdn Bhd",
      "url": "https://www.sedistribution.com.my"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Hytera Body-Worn Camera Products — SE Distribution Malaysia",
    "description": "Hytera body-worn cameras available through SE Distribution, authorized Hytera distributor in Malaysia.",
    "url": "https://www.sedistribution.com.my/solutions/surveillance",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "item": { "@type": "Product", "name": "Hytera SC880 5G Body Camera", "description": "5G flagship — 4K video, 300s pre-record, 10-hr battery, IP68, MIL-STD-810G, AES-256", "brand": { "@type": "Brand", "name": "Hytera" } } },
      { "@type": "ListItem", "position": 2, "item": { "@type": "Product", "name": "Hytera SC780 4G Body Camera", "description": "4G LTE, 1080P, 130° FOV, 13-hr battery, 120s pre-record, IP68, live streaming", "brand": { "@type": "Brand", "name": "Hytera" } } },
      { "@type": "ListItem", "position": 3, "item": { "@type": "Product", "name": "Hytera GC550 Body Camera", "description": "Compact, 1080P, 8-hr battery, IP67, MIL-STD-810G, IR night vision, AES-256", "brand": { "@type": "Brand", "name": "Hytera" } } },
      { "@type": "ListItem", "position": 4, "item": { "@type": "Product", "name": "Hytera DEMS Evidence Platform", "description": "Digital Evidence Management System — chain-of-custody, AI search, audit trail, cloud upload", "brand": { "@type": "Brand", "name": "Hytera" } } }
    ]
  }
];

export default function SurveillancePage() {
  useSEO({
    title: "Surveillance & Security Solutions Malaysia | Hytera Body Camera | SE Distribution",
    description: "Malaysia's leading surveillance solutions — Hytera body-worn cameras (SC880 5G, SC780 4G, GC550) with DEMS evidence management. Authorized distributor in Kuala Lumpur. Request a demo today.",
    canonical: "https://www.sedistribution.com.my/solutions/surveillance",
    keywords: "body worn camera Malaysia, Hytera body camera, surveillance solution Malaysia, AI CCTV Malaysia, law enforcement camera, security camera Malaysia, SE Distribution",
    structuredData: surveillanceStructuredData,
  });
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[480px] md:h-[580px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20security%20command%20center%20multiple%20screens%20surveillance%20footage%20analysts%20working%20night%20shift%20dramatic%20lighting%20cinematic%20wide%20angle%20modern%20technology&width=1400&height=700&seq=surv-hero&orientation=landscape"
          alt="Surveillance Solutions"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20"></div>
        <div className="absolute inset-0 flex flex-col justify-center px-6 lg:px-20 max-w-7xl mx-auto w-full">
          <nav className="flex items-center gap-2 text-xs text-white/50 mb-6">
            <Link to="/" className="hover:text-white/80 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/solutions" className="hover:text-white/80 transition-colors">Solutions</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/80">Surveillance & Security</span>
          </nav>
          <span className="text-xs font-semibold tracking-widest uppercase text-white/50 mb-3">Solutions</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-2xl">
            Surveillance &amp; Security Solutions
          </h1>
          <p className="text-lg text-white/75 max-w-xl leading-relaxed mb-8">
            Body-worn cameras and mission-critical evidence management — powered by Hytera, Malaysia&apos;s leading body-worn video solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 w-fit"
            >
              Request a Demo <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/brands"
              className="border border-white/50 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 w-fit"
            >
              View Brand Profiles
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1a1a1a] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-white mb-1">{s.value}</p>
              <p className="text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Hytera BWV Spotlight Banner */}
      <section className="py-10 px-6 lg:px-20 bg-[#f0ede8]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-8 bg-white rounded-xl p-8 border border-[#1a1a1a]/10">
            <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-[#1a1a1a] rounded-xl">
              <i className="ri-vidicon-line text-white text-2xl"></i>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#1a1a1a]/5 rounded-full px-3 py-1 mb-2">
                <i className="ri-star-fill text-[#1a1a1a] text-xs"></i>
                <span className="text-xs font-semibold text-[#1a1a1a] uppercase tracking-widest">Featured Product</span>
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-1">Hytera BWC — Malaysia's Primary Body-Worn Camera Solution</h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-2xl">
                Hytera is SE Distribution's primary choice for body-worn video in Malaysia. The SC880 5G flagship delivers 4K video and 300-second pre-record, the SC780 4G offers 13-hour battery life with live streaming, and the GC550 provides a compact reliable option — all purpose-built for law enforcement, government agencies, and private security operations.
              </p>
            </div>
            <Link
              to="/hytera-bwv"
              className="flex-shrink-0 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              View Hytera BWC Spotlight <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 px-6 lg:px-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Overview</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight mb-5">
              Complete Security Ecosystems, Not Just Cameras
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-4">
              SE Distribution is the authorized Hytera distributor in Malaysia, delivering complete body-worn camera solutions tailored to your operational requirements. Whether you are deploying 10 body cameras or equipping an entire enforcement division, we provide the hardware, software integration, and ongoing support to make it work.
            </p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              Our solutions combine professional mobile radio communications, AI-driven fixed cameras, body-worn recording devices, and centralised evidence management into a unified architecture — removing the integration headaches and vendor fragmentation that security teams commonly face.
            </p>
            <div className="flex flex-wrap gap-3">
              {["Hytera"].map((b) => (
                <span key={b} className="text-xs font-semibold bg-[#f5f3ee] text-[#1a1a1a] px-4 py-1.5 rounded-full">
                  {b}
                </span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="w-full h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=security%20officer%20wearing%20body%20camera%20on%20patrol%20urban%20street%20at%20night%20professional%20law%20enforcement%20equipment%20tactical%20gear%2C%20cinematic%20lighting%2C%20realistic%20photography%2C%20high%20detail&width=700&height=500&seq=surv-overview&orientation=landscape"
                alt="Security officer with body camera"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-lg px-5 py-4 border border-gray-100 w-52">
              <p className="text-xs text-gray-400 mb-1">Brands Authorized</p>
              <div className="flex gap-2 items-center">
                <span className="text-sm font-bold text-[#1a1a1a]">Hytera — Authorized Distributor</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-[#f8f7f4] py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Capabilities</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">What Our Solutions Deliver</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-lg p-7 hover:border-[#1a1a1a]/20 border border-transparent transition-colors">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                  <i className={`${f.icon} text-xl text-[#1a1a1a]`}></i>
                </div>
                <h3 className="font-bold text-[#1a1a1a] text-base mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Product Highlights</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">Featured Devices &amp; Systems</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.name} className="rounded-lg overflow-hidden bg-[#f8f7f4] group cursor-pointer">
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{p.brand}</span>
                    {p.relationship === "distributor" ? (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#1a1a1a] text-white px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        <i className="ri-verified-badge-line"></i> Authorized
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#f5f3ee] text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        <i className="ri-shake-hands-line"></i> Tech Partner
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-[#1a1a1a] text-base mb-2">{p.name}</h3>
                  <p className="text-xs text-gray-500">{p.spec}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-[#1a1a1a] py-16 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3 block">Industries Served</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white">Who We Serve</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {industries.map((ind) => (
              <div key={ind.name} className="flex flex-col items-center gap-2 text-center py-5 px-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${ind.icon} text-2xl text-white/70`}></i>
                </div>
                <p className="text-xs text-white/60">{ind.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
            Ready to Build Your Security Infrastructure?
          </h2>
          <p className="text-gray-500 text-base mb-8 max-w-xl mx-auto">
            Talk to our solutions team today. We&apos;ll assess your environment and recommend the right combination of devices and systems for your operation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#1a1a1a] text-white text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
            >
              Talk With Us <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/brands"
              className="border border-[#1a1a1a] text-[#1a1a1a] text-sm font-medium px-8 py-3.5 rounded-md hover:bg-[#f8f7f4] transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Brands
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
